<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { Column, DataTable } from 'primevue';
import ColumnSelectorButton from 'shared/common/components/ColumnSelectorButton.vue';
import { useColumnSelector, type ColumnField } from 'shared/common/composables/useColumnSelector';
import { useFieldUnit, useWeightUnit } from 'shared/common/composables/fieldUnit';
import { formatDuration } from 'shared/common/domain/helperFunctions';
import { formatMeasurement } from 'shared/common/domain/units';
import { formatWeight } from 'shared/common/domain/weightUnits';
import { usePackageStore } from 'shared/packages/stores/packageStore';
import { usePackageTypeStore } from 'shared/packages/stores/packageTypeStore';
import { useProductGroupStore } from 'shared/products/groups/stores/productGroupStore';
import { useProductStore } from 'shared/products/stores/productStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useLanguageStore } from 'shared/translations/stores/languageStore';

const props = defineProps<{
  packageId: string;
}>();

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const t = computed(() => translations.value.packages.details);
const fields = computed(() => translations.value.packages.fields);
const groups = computed(() => translations.value.packages.groups);

const packageStore = usePackageStore();
const packageTypeStore = usePackageTypeStore();
const productStore = useProductStore();
const productGroupStore = useProductGroupStore();
const storageAreaStore = useStorageAreaStore();

// Shares the unit preferences chosen in the package list and edit dialog.
const units = {
  height: useFieldUnit('package-height'),
  nominalHeight: useFieldUnit('package-nominal-height'),
  width: useFieldUnit('package-width'),
  nominalWidth: useFieldUnit('package-nominal-width'),
  length: useFieldUnit('package-length'),
  nominalLength: useFieldUnit('package-nominal-length'),
  weight: useWeightUnit('package-weight'),
  nominalWeight: useWeightUnit('package-nominal-weight'),
};

const pkg = computed(() => packageStore.getById(props.packageId) ?? null);

const empty = '—';

function num(value: number | null | undefined, digits: number, suffix = ''): string {
  if (value == null) return empty;
  return `${languageStore.formatNumber(value, digits)}${suffix}`;
}

function measurement(value: number | null | undefined, unit: Parameters<typeof formatMeasurement>[1]): string {
  return value == null ? empty : formatMeasurement(value, unit);
}

function weight(value: number | null | undefined, unit: Parameters<typeof formatWeight>[1]): string {
  return value == null ? empty : formatWeight(value, unit);
}

function volume(value: number | null | undefined): string {
  return num(value, 3, ` ${translations.value.units.cubicMeter}`);
}

const qualities = computed(() => {
  const contents = pkg.value?.contents ?? [];
  const distinct = new Set<string>();
  for (const content of contents) {
    const quality = content.quality ?? (content.productId ? productStore.getById(content.productId)?.quality : null);
    if (quality) distinct.add(quality);
  }
  return distinct.size > 0 ? [...distinct].join(', ') : empty;
});

const ageInDays = computed(() => {
  const createdAt = pkg.value?.createdAtUtc;
  if (!createdAt) return null;
  return Math.floor((Date.now() - Date.parse(createdAt)) / 86_400_000);
});

const summaryRows = computed(() => {
  const p = pkg.value;
  if (!p) return [];
  return [
    { label: fields.value.number, value: p.number || empty },
    { label: fields.value.externalNumber, value: p.externalNumber || empty },
    { label: t.value.status, value: p.status },
    { label: t.value.packageType, value: (p.packageTypeId ? packageTypeStore.getById(p.packageTypeId)?.name : null) ?? empty },
    { label: fields.value.composition, value: translations.value.packages.compositions[p.composition] ?? p.composition ?? empty },
    { label: t.value.storageArea, value: (p.packageStorageAreaId ? storageAreaStore.getById(p.packageStorageAreaId)?.name : null) ?? empty },
    { label: fields.value.stackLevel, value: num(p.stackLevel, 0) },
    { label: t.value.quality, value: qualities.value },
    { label: fields.value.createdAt, value: p.createdAtUtc ? languageStore.formatDateTime(p.createdAtUtc) : empty },
    { label: t.value.age, value: ageInDays.value == null ? empty : t.value.ageDays(ageInDays.value) },
    { label: fields.value.comment, value: p.comment || empty },
  ];
});

const dimensionRows = computed(() => {
  const p = pkg.value;
  if (!p) return [];
  return [
    { label: fields.value.volume, physical: volume(p.volume), nominal: volume(p.nominalVolume) },
    { label: fields.value.runningMeters, physical: num(p.runningMeters, 3, ' m'), nominal: num(p.nominalRunningMeters, 3, ' m') },
    { label: fields.value.weight, physical: weight(p.weight, units.weight.value), nominal: weight(p.nominalWeight, units.nominalWeight.value) },
    { label: fields.value.height, physical: measurement(p.height, units.height.value), nominal: measurement(p.nominalHeight, units.nominalHeight.value) },
    { label: fields.value.width, physical: measurement(p.width, units.width.value), nominal: measurement(p.nominalWidth, units.nominalWidth.value) },
    { label: fields.value.length, physical: measurement(p.length, units.length.value), nominal: measurement(p.nominalLength, units.nominalLength.value) },
  ];
});

const statisticRows = computed(() => {
  const p = pkg.value;
  if (!p) return [];
  return [
    { label: fields.value.timesMovedCount, value: num(p.timesMovedCount, 0) },
    { label: fields.value.distanceTravelledKm, value: num(p.distanceTravelledMetres / 1000, 3, ' km') },
    { label: fields.value.timeCarried, value: formatDuration(p.timeCarriedSeconds) },
    { label: fields.value.weightedDistanceTravelledKm, value: num(p.weightedDistanceTravelledMetres / 1000, 3, ' km') },
  ];
});

const contentColumns = computed<ColumnField[]>(() => [
  { field: 'product', header: t.value.product },
  { field: 'productCode', header: t.value.productCode },
  { field: 'productGroup', header: t.value.productGroup },
  { field: 'quality', header: t.value.quality },
  { field: 'pieces', header: t.value.pieces },
  { field: 'thickness', header: t.value.thickness },
  { field: 'nominalThickness', header: t.value.nominalThickness },
  { field: 'width', header: fields.value.width },
  { field: 'nominalWidth', header: fields.value.nominalWidth },
  { field: 'length', header: fields.value.length },
  { field: 'nominalLength', header: fields.value.nominalLength },
  { field: 'volume', header: fields.value.volume },
  { field: 'nominalVolume', header: fields.value.nominalVolume },
  { field: 'weight', header: fields.value.weight },
  { field: 'nominalWeight', header: fields.value.nominalWeight },
  { field: 'runningMeters', header: fields.value.runningMeters },
  { field: 'nominalRunningMeters', header: fields.value.nominalRunningMeters },
  { field: 'moisture', header: t.value.moisture },
  { field: 'partNumber', header: t.value.partNumber },
  { field: 'codes', header: t.value.codes },
]);

const { orderedColumns, selectedColumns, setSelectedColumns, setOrder, reset } = useColumnSelector({
  storageKey: 'package-details-contents-columns',
  columns: contentColumns,
  defaultFields: ['product', 'quality', 'pieces', 'thickness', 'width', 'length', 'volume'],
});

const contentRows = computed(() => {
  const contents = [...(pkg.value?.contents ?? [])].sort((a, b) => a.order - b.order);
  return contents.map((content) => {
    const product = content.productId ? productStore.getById(content.productId) ?? null : null;
    const group = product?.productGroupId ? productGroupStore.getById(product.productGroupId) ?? null : null;
    return {
      id: content.id,
      product: product?.name ?? empty,
      productCode: product?.externalCode ?? empty,
      productGroup: group?.name ?? empty,
      quality: content.quality ?? product?.quality ?? empty,
      pieces: num(content.pieces, 0),
      thickness: measurement(content.thickness, 'mm'),
      nominalThickness: measurement(content.nominalThickness, 'mm'),
      width: measurement(content.width, 'mm'),
      nominalWidth: measurement(content.nominalWidth, 'mm'),
      length: measurement(content.length, 'mm'),
      nominalLength: measurement(content.nominalLength, 'mm'),
      volume: volume(content.volume),
      nominalVolume: volume(content.nominalVolume),
      weight: weight(content.weight, 'kg'),
      nominalWeight: weight(content.nominalWeight, 'kg'),
      runningMeters: num(content.runningMeters, 3, ' m'),
      nominalRunningMeters: num(content.nominalRunningMeters, 3, ' m'),
      moisture: num(content.moisture, 1, ' %'),
      partNumber: content.partNumber || empty,
      codes: content.codes.length > 0 ? content.codes.map((code) => code.code).join(', ') : empty,
    } as Record<string, string>;
  });
});
</script>

<template>
  <div v-if="pkg" class="package-details-panel">
    <section class="details-section">
      <h3 class="section-title">{{ t.summary }}</h3>
      <dl class="details-grid">
        <template v-for="row in summaryRows" :key="row.label">
          <dt>{{ row.label }}</dt>
          <dd>{{ row.value }}</dd>
        </template>
      </dl>
    </section>

    <section class="details-section">
      <h3 class="section-title">{{ t.dimensions }}</h3>
      <table class="dimension-table">
        <thead>
          <tr>
            <th></th>
            <th>{{ groups.physical }}</th>
            <th>{{ groups.nominal }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in dimensionRows" :key="row.label">
            <th>{{ row.label }}</th>
            <td>{{ row.physical }}</td>
            <td>{{ row.nominal }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section class="details-section">
      <header class="section-header">
        <h3 class="section-title">{{ t.contents }}</h3>
        <ColumnSelectorButton :columns="orderedColumns" :model-value="selectedColumns"
          @update:model-value="setSelectedColumns" @reorder="setOrder" @reset="reset"></ColumnSelectorButton>
      </header>
      <p v-if="contentRows.length === 0" class="empty-state">{{ t.noContents }}</p>
      <DataTable v-else :value="contentRows" data-key="id" size="small" scrollable>
        <Column v-for="col of selectedColumns" :key="col.field" :field="col.field" :header="col.header"></Column>
      </DataTable>
    </section>

    <section class="details-section">
      <h3 class="section-title">{{ groups.statistics }}</h3>
      <dl class="details-grid">
        <template v-for="row in statisticRows" :key="row.label">
          <dt>{{ row.label }}</dt>
          <dd>{{ row.value }}</dd>
        </template>
      </dl>
    </section>
  </div>
</template>

<style scoped>
.package-details-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.75rem;
}

.details-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.section-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.details-grid {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 0.25rem 1rem;
  margin: 0;
}

.details-grid dt {
  color: var(--p-text-muted-color);
}

.details-grid dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.dimension-table {
  width: 100%;
  border-collapse: collapse;
}

.dimension-table th,
.dimension-table td {
  padding: 0.25rem 0.5rem 0.25rem 0;
  text-align: left;
}

.dimension-table tbody th {
  font-weight: normal;
  color: var(--p-text-muted-color);
}

.empty-state {
  margin: 0;
  color: var(--p-text-muted-color);
}
</style>
